import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Input,
  ListBox,
  Select,
  TextArea,
  TextField,
} from '@heroui/react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { TaskTemplate, TaskType } from '@/lib/types';
import {
  TASK_TYPE_COLORS,
  TASK_TYPE_DESCRIPTIONS,
  TASK_TYPE_ICONS,
  TASK_TYPE_LABELS,
  TASK_TYPE_OPTIONS,
} from '@/lib/taskType';
import { PROPOSED_TASKS } from '@/lib/taskCatalog';
import { FieldLabel } from '@/components/molecules/FieldLabel';
import { MarkdownTextarea } from '@/components/molecules/MarkdownTextarea';

/** Патч полей задачи — правки уходят в БД сразу (snake_case в хуке страницы) */
export interface TaskPatch {
  title?: string;
  type?: TaskType;
  instruction?: string;
  aiPrompts?: string[];
  isRequired?: boolean;
}

interface TemplateTasksProps {
  tasks: TaskTemplate[];
  /** Просмотр: список без полей и действий */
  readOnly: boolean;
  /** Идёт запрос к БД — контролы выключены */
  isMutating?: boolean;
  onAddTask: (type: TaskType) => void;
  onUpdateTask: (taskId: string, patch: TaskPatch) => void;
  onDeleteTask: (taskId: string) => void;
  onMoveTask: (taskId: string, dir: -1 | 1) => void;
}

/** Тонированная подложка иконки — в цвет Chip'а типа задачи */
const TASK_TYPE_TINTS: Record<TaskType, string> = {
  photo: 'bg-warning/15 text-warning',
  text_input: 'bg-accent/15 text-accent',
  checkbox: 'bg-success/15 text-success',
  action: 'bg-foreground/10 text-foreground',
};

/** Иконка типа задачи в тонированном квадратике; tooltip — название типа */
function TaskTypeIcon({ type, square = 'md' }: { type: TaskType; square?: 'sm' | 'md' }) {
  const Icon = TASK_TYPE_ICONS[type];
  return (
    <span
      title={TASK_TYPE_LABELS[type]}
      className={cn(
        'flex shrink-0 items-center justify-center',
        square === 'sm' ? 'size-6 rounded-md' : 'size-8 rounded-lg',
        TASK_TYPE_TINTS[type],
      )}
    >
      <Icon className={square === 'sm' ? 'size-3.5' : 'size-4'} />
    </span>
  );
}

/** Read-only карточка задачи (режим просмотра): номер, иконка типа,
 *  название, сниппет подсказки и число ИИ-промптов */
function TaskRow({ index, task }: { index: number; task: TaskTemplate }) {
  // Сниппет подсказки: markdown показывать некрасиво — сырой текст в строку
  const instruction = task.instruction?.replace(/\s+/g, ' ').trim();
  const promptsCount = task.aiPrompts?.length ?? 0;

  return (
    <div className="flex items-start gap-3 rounded-[var(--radius)] bg-surface-secondary px-3 py-2.5">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-xs font-semibold text-accent">
        {index + 1}
      </span>
      <TaskTypeIcon type={task.type} square="sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="min-w-0 break-words text-sm font-medium text-foreground">
            {task.title}
          </span>
          <Chip size="sm" variant="soft" color={TASK_TYPE_COLORS[task.type]}>
            {TASK_TYPE_LABELS[task.type]}
          </Chip>
          {!task.isRequired && <span className="text-xs text-muted">· необязательная</span>}
        </div>
        {instruction && <p className="line-clamp-2 text-xs leading-5 text-muted">{instruction}</p>}
        {promptsCount > 0 && <p className="text-xs text-muted">{pluralPrompts(promptsCount)}</p>}
      </div>
    </div>
  );
}

/** «1 ИИ-промпт / 2 ИИ-промпта / 5 ИИ-промптов» */
function pluralPrompts(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ИИ-промпт`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ИИ-промпта`;
  return `${n} ИИ-промптов`;
}

/** Тулбар добавления: заготовки по типам задач (иконка + название типа) */
function AddTaskToolbar({
  isMutating,
  onAddTask,
}: {
  isMutating: boolean;
  onAddTask: (type: TaskType) => void;
}) {
  return (
    <div className="flex flex-col gap-2" role="toolbar" aria-label="Добавить задачу">
      <span className="text-sm font-medium text-foreground">Добавить задачу</span>
      <div className="flex flex-wrap gap-2">
        {PROPOSED_TASKS.map((proposed) => {
          const Icon = TASK_TYPE_ICONS[proposed.type];
          return (
            <button
              key={proposed.key}
              type="button"
              title={TASK_TYPE_DESCRIPTIONS[proposed.type]}
              disabled={isMutating}
              onClick={() => onAddTask(proposed.type)}
              className="group flex items-center gap-2.5 rounded-[var(--radius)] border border-border bg-surface px-3 py-2 text-left transition-colors hover:border-accent hover:bg-surface-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:pointer-events-none disabled:opacity-50"
            >
              <span
                className={cn(
                  'flex size-8 shrink-0 items-center justify-center rounded-lg',
                  TASK_TYPE_TINTS[proposed.type],
                )}
              >
                <Icon className="size-4" />
              </span>
              <span className="text-sm font-medium text-foreground">{proposed.title}</span>
              <Plus className="size-4 text-muted transition-colors group-hover:text-accent" />
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Карточка задачи в режиме правки. Поля держат локальный draft; коммит —
 * сразу в БД: текстовые на blur (только если отличаются от последнего
 * отправленного значения), селект типа и чекбокс — мгновенно. Серверный
 * refetch (добавление/ошибка) синхронизирует draft; собственные правки
 * PATCH'атся без refetch, поэтому ввод не сбрасывается.
 */
function TaskCard({
  task,
  index,
  total,
  isMutating,
  onUpdate,
  onDelete,
  onMove,
}: {
  task: TaskTemplate;
  index: number;
  total: number;
  isMutating: boolean;
  onUpdate: (taskId: string, patch: TaskPatch) => void;
  onDelete: (taskId: string) => void;
  onMove: (taskId: string, dir: -1 | 1) => void;
}) {
  const aiPromptsText = (task.aiPrompts ?? []).join('\n');
  const [title, setTitle] = useState(task.title);
  const [instruction, setInstruction] = useState(task.instruction ?? '');
  const [aiPrompts, setAiPrompts] = useState(aiPromptsText);
  const [titleError, setTitleError] = useState<string | null>(null);
  // Последнее отправленное на сервер значение — коммит только при отличии
  // (пропсы задачи между правками не обновляются, сверяться с ними нельзя)
  const committed = useRef({
    title: task.title,
    instruction: task.instruction ?? '',
    aiPrompts: aiPromptsText,
  });

  // Синхронизация после серверного refetch / оптимистичного патча пропсов:
  // перезаписываем draft только полями, которые на сервере отличаются от
  // последнего коммита (внешнее изменение или откат ошибки) — так ввод
  // в других полях и несмётченные правки не сбрасываются
  useEffect(() => {
    const server = {
      title: task.title,
      instruction: task.instruction ?? '',
      aiPrompts: (task.aiPrompts ?? []).join('\n'),
    };
    if (server.title !== committed.current.title) {
      setTitle(server.title);
      committed.current.title = server.title;
    }
    if (server.instruction !== committed.current.instruction) {
      setInstruction(server.instruction);
      committed.current.instruction = server.instruction;
    }
    if (server.aiPrompts !== committed.current.aiPrompts) {
      setAiPrompts(server.aiPrompts);
      committed.current.aiPrompts = server.aiPrompts;
    }
    setTitleError(null);
  }, [task]);

  const commitTitle = () => {
    const value = title.trim();
    if (value === '') {
      setTitleError('Укажите название задачи');
      return;
    }
    setTitleError(null);
    if (value !== committed.current.title) {
      committed.current.title = value;
      onUpdate(task.id, { title: value });
    }
  };

  const commitInstruction = () => {
    if (instruction !== committed.current.instruction) {
      committed.current.instruction = instruction;
      onUpdate(task.id, { instruction });
    }
  };

  const commitAiPrompts = () => {
    // Нормализация та же, что в хуке: по строке на промпт, без пустых
    const value = aiPrompts
      .split('\n')
      .map((p) => p.trim())
      .filter(Boolean);
    const text = value.join('\n');
    if (text !== committed.current.aiPrompts) {
      committed.current.aiPrompts = text;
      onUpdate(task.id, { aiPrompts: value });
    }
  };

  // action — шаг без сбора данных: обязательность к нему не применяется
  const isAction = task.type === 'action';

  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius)] border border-border p-3">
      {/* Заголовок: номер, тип и действия — сортировка и удаление */}
      <div className="flex items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-accent/15 text-sm font-semibold text-accent">
          {index + 1}
        </span>
        <TaskTypeIcon type={task.type} />
        <Chip size="sm" variant="soft" color={TASK_TYPE_COLORS[task.type]}>
          {TASK_TYPE_LABELS[task.type]}
        </Chip>
        <span className="min-w-0 flex-1" />
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            aria-label="Переместить выше"
            isDisabled={isMutating || index === 0}
            onPress={() => onMove(task.id, -1)}
          >
            <ChevronUp className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Переместить ниже"
            isDisabled={isMutating || index === total - 1}
            onPress={() => onMove(task.id, 1)}
          >
            <ChevronDown className="size-4" />
          </Button>
          <Button
            variant="danger-soft"
            size="sm"
            aria-label="Удалить задачу"
            isDisabled={isMutating}
            onPress={() => onDelete(task.id)}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_190px]">
        <div className="flex flex-col gap-1">
          <TextField
            variant="secondary"
            value={title}
            onChange={setTitle}
            isRequired
            isInvalid={Boolean(titleError)}
          >
            <FieldLabel>Название</FieldLabel>
            <Input placeholder="Например: Фото полки" onBlur={commitTitle} />
          </TextField>
          {/* Слот ошибки фиксированной высоты — как у названия шаблона */}
          <p className="min-h-5 text-sm text-danger" aria-live={titleError ? 'polite' : undefined}>
            {titleError ?? ''}
          </p>
        </div>
        <Select
          className="w-full"
          variant="secondary"
          value={task.type}
          onChange={(key) => {
            const next = key == null ? '' : String(key);
            if (next && next !== task.type) onUpdate(task.id, { type: next as TaskType });
          }}
        >
          <FieldLabel>Тип</FieldLabel>
          <Select.Trigger>
            <Select.Value />
            <Select.Indicator />
          </Select.Trigger>
          <Select.Popover>
            <ListBox>
              {TASK_TYPE_OPTIONS.map((option) => (
                <ListBox.Item key={option.id} id={option.id} textValue={option.label}>
                  {option.label}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              ))}
            </ListBox>
          </Select.Popover>
        </Select>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Checkbox
          variant="secondary"
          isSelected={task.isRequired}
          isDisabled={isMutating || isAction}
          onChange={(selected) => onUpdate(task.id, { isRequired: selected })}
        >
          <Checkbox.Content>
            <Checkbox.Control>
              <Checkbox.Indicator />
            </Checkbox.Control>
            Обязательная
          </Checkbox.Content>
        </Checkbox>
        {isAction && (
          <span className="text-xs text-muted">
            шаг без сбора данных — обязательность не применяется
          </span>
        )}
      </div>

      <MarkdownTextarea
        label="Подсказка для исполнителя"
        value={instruction}
        onChange={setInstruction}
        onBlur={commitInstruction}
        placeholder="Например: Сфотографируйте полку целиком, чтобы был виден ценник"
        minHeight={64}
      />

      <div className="flex flex-col gap-1.5">
        <FieldLabel>ИИ-промпты</FieldLabel>
        <TextArea
          variant="secondary"
          aria-label="ИИ-промпты"
          className="resize-y"
          style={{ minHeight: 56 }}
          value={aiPrompts}
          onChange={(e) => setAiPrompts(e.target.value)}
          onBlur={commitAiPrompts}
          placeholder="Есть ли на фото товар «Первая скрипка»?"
        />
        <p className="text-xs text-muted">
          По одному промпту на строку — каждый выполняется отдельным ИИ-запросом
        </p>
      </div>
    </div>
  );
}

/**
 * Карточка «Задачи» страницы шаблона. В просмотре — read-only список;
 * в правке — тулбар добавления по типам и карточки задач. Каждая операция
 * (добавить/изменить/удалить/переместить) сразу уходит в БД — как фото.
 */
export function TemplateTasks({
  tasks,
  readOnly,
  isMutating = false,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  onMoveTask,
}: TemplateTasksProps) {
  return (
    <Card className="bg-surface">
      <Card.Header>
        <Card.Title className="text-lg font-semibold">Задачи ({tasks.length})</Card.Title>
      </Card.Header>
      <Card.Content className="flex flex-col gap-3">
        {readOnly ? (
          tasks.length === 0 ? (
            <p className="rounded-[var(--radius)] border border-dashed border-border px-4 py-6 text-center text-sm text-muted">
              В шаблоне нет задач — для запуска нужна хотя бы одна.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {tasks.map((task, index) => (
                <TaskRow key={task.id} index={index} task={task} />
              ))}
            </div>
          )
        ) : (
          <>
            <AddTaskToolbar isMutating={isMutating} onAddTask={onAddTask} />
            {tasks.length === 0 ? (
              <p className="rounded-[var(--radius)] border border-dashed border-border px-4 py-6 text-center text-sm text-muted">
                Задачи ещё не добавлены — выберите тип выше, чтобы добавить первую.
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {tasks.map((task, index) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    index={index}
                    total={tasks.length}
                    isMutating={isMutating}
                    onUpdate={onUpdateTask}
                    onDelete={onDeleteTask}
                    onMove={onMoveTask}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </Card.Content>
    </Card>
  );
}
